import { layoutOf } from '@ayumia/component-runtime';
import { text } from '../../../app/i18n';
import { removeAt } from '../../editor/json-pointer';
import { declaredPages, setPlacement, slugId, updateLayout, type FormLayout, type Template } from '../layout-model';

/**
 * Pages, bandeau du haut et barre du bas dans l'éditeur v2 (ADR-146 addendum (14) « Pages et champs », planches
 * « EditeurV2-Pages », « -Copier », « -BandeauHaut », « -BarreBas »). Les champs vivent dans le formulaire ; une
 * page ne fait que placer des parties. Supprimer une page laisse ses parties « À placer » (elles s'affichent en
 * fin de dernière page, règle du Core), sauf si l'on supprime aussi ses champs.
 */

type Section = Template['sections'][number];
type Header = NonNullable<FormLayout['header']>;
type Footer = NonNullable<FormLayout['footer']>;

const clone = <T>(value: T): T => structuredClone(value);

/** Retire une partie et ses réglages de mise en page (placement, pages). */
export function removeSection(template: Template, sectionId: string): Template {
  const s = template.sections.findIndex((section) => section.section_id === sectionId);
  if (s < 0) return template;
  const without = removeAt(template, `/sections/${s}`);
  if (layoutOf(without) === undefined) return without;
  let next = setPlacement(without, sectionId, { region: undefined, card: undefined, collapsible: undefined, columns: undefined, emphasis: undefined, size: undefined, readonly_when: undefined, presentation: undefined, entry_panel: undefined });
  next = updateLayout(next, (layout) => {
    const pages = layout.pages?.map((page) => ({ ...page, sections: page.sections.filter((id) => id !== sectionId) })).filter((page) => page.sections.length > 0);
    return { ...layout, pages: pages === undefined || pages.length === 0 ? undefined : pages };
  });
  return next;
}

/** Supprime une page : ses parties vont « À placer », ou disparaissent avec elle. */
export function deletePageV2(template: Template, pageId: string, withFields: boolean): Template {
  const page = declaredPages(template).find((candidate) => candidate.page_id === pageId);
  if (page === undefined) return template;
  let next = updateLayout(template, (layout) => {
    const pages = (layout.pages ?? []).filter((candidate) => candidate.page_id !== pageId);
    // Un bloc qui ne paraissait que sur cette page devient persistant plutôt que de citer une page absente.
    const blocks = (layout.blocks ?? []).map((block) => {
      if (block.page_ids === undefined) return block;
      const kept = block.page_ids.filter((id) => id !== pageId);
      const copy = { ...block } as typeof block & Record<string, unknown>;
      if (kept.length === 0) delete copy['page_ids'];
      else copy.page_ids = kept as typeof block.page_ids;
      return copy;
    });
    return { ...layout, pages: pages.length === 0 ? undefined : pages, blocks: blocks.length === 0 ? undefined : blocks };
  });
  if (withFields) for (const sectionId of page.sections) next = removeSection(next, sectionId);
  // Une partie au moins reste dans le formulaire (contrat) : la dernière n'est jamais retirée.
  return next.sections.length === 0 ? template : next;
}

/** Identifiants déjà pris dans le gabarit (parties et champs). */
function takenIds(template: Template): Set<string> {
  const taken = new Set<string>();
  for (const section of template.sections) {
    taken.add(section.section_id);
    for (const entry of [...section.fields, ...(section.repeat?.children?.fields ?? [])]) taken.add(entry.field.field_id);
  }
  return taken;
}

/** Copie d'une partie, ses champs et elle-même renommés pour rester uniques. */
function copySection(template: Template, section: Section): Section {
  const taken = takenIds(template);
  const fresh = (base: string): string => {
    const stem = base.replace(/_d+$/, '');
    let index = 2;
    while (taken.has(`${stem}_${index}`)) index += 1;
    taken.add(`${stem}_${index}`);
    return `${stem}_${index}`;
  };
  const copy = clone(section);
  copy.section_id = fresh(section.section_id);
  copy.fields = copy.fields.map((entry) => ({ ...entry, field: { ...entry.field, field_id: fresh(entry.field.field_id) } })) as Section['fields'];
  if (copy.repeat?.children?.fields !== undefined) copy.repeat.children.fields = copy.repeat.children.fields.map((entry) => ({ ...entry, field: { ...entry.field, field_id: fresh(entry.field.field_id) } })) as NonNullable<NonNullable<Section['repeat']>['children']>['fields'];
  return copy;
}

/** Duplique une page sous un nouveau nom : ses parties sont copiées (champs renommés), placées juste après. */
export function duplicatePage(template: Template, pageId: string, label: string): { template: Template; id: string } {
  const pages = declaredPages(template);
  const index = pages.findIndex((page) => page.page_id === pageId);
  const page = pages[index];
  if (page === undefined) return { template, id: pageId };
  let next = template;
  const sectionIds: string[] = [];
  for (const sectionId of page.sections) {
    const section = next.sections.find((candidate) => candidate.section_id === sectionId);
    if (section === undefined) continue;
    const copy = copySection(next, section);
    next = { ...next, sections: [...next.sections, copy] };
    const placement = layoutOf(next)?.placements?.[sectionId];
    if (placement !== undefined) next = updateLayout(next, (layout) => ({ ...layout, placements: { ...(layout.placements ?? {}), [copy.section_id]: clone(placement) } }));
    sectionIds.push(copy.section_id);
  }
  const id = slugId(label, new Set(pages.map((candidate) => candidate.page_id)), 'page');
  next = updateLayout(next, (layout) => {
    const list = [...(layout.pages ?? [])];
    list.splice(index + 1, 0, { ...clone(page), page_id: id, label: { ...(page.label as Record<string, string>), fr: label }, sections: sectionIds as [string, ...string[]] });
    return { ...layout, pages: list };
  });
  return { template: next, id };
}

/** Champs d'une page, en libellés (boîte de suppression). */
export function pageFieldLabels(template: Template, pageId: string): string[] {
  const page = declaredPages(template).find((candidate) => candidate.page_id === pageId);
  if (page === undefined) return [];
  return page.sections.flatMap((id) => template.sections.find((section) => section.section_id === id)?.fields.map((entry) => text(entry.field.label, 'fr', 'Champ')) ?? []);
}

/** Réglages du bandeau du haut (`layout.header`). */
export function setHeader(template: Template, patch: Partial<Record<keyof Header, unknown>>): Template {
  return updateLayout(template, (layout) => {
    const header = { ...(layout.header ?? {}) } as Record<string, unknown>;
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete header[key];
      else header[key] = value;
    }
    return { ...layout, header: Object.keys(header).length === 0 ? undefined : header };
  });
}

/** Raccourci « Valider » du bandeau. */
export function setShortcut(template: Template, patch: Record<string, unknown>): Template {
  const current = { ...((layoutOf(template)?.header as Header | undefined)?.submit_shortcut ?? {}) } as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete current[key];
    else current[key] = value;
  }
  return setHeader(template, { submit_shortcut: Object.keys(current).length === 0 ? undefined : current });
}

/** Réglages de la barre du bas (`layout.footer`). */
export function setFooter(template: Template, patch: Partial<Record<keyof Footer, unknown>>): Template {
  return updateLayout(template, (layout) => {
    const footer = { ...(layout.footer ?? {}) } as Record<string, unknown>;
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete footer[key];
      else footer[key] = value;
    }
    return { ...layout, footer: Object.keys(footer).length === 0 ? undefined : footer };
  });
}

/** Formulaire (`visual`) : image de la médiathèque ou icône, fond aux jetons. */
export function setVisual(template: Template, visual: Template['visual'] | undefined): Template {
  const next = { ...template } as Template & Record<string, unknown>;
  if (visual === undefined || Object.keys(visual).length === 0) delete next['visual'];
  else next.visual = visual;
  return next;
}

/**
 * Une seule action principale visible (gate `LAYOUT_PRIMARY_ACTION_MULTIPLE`) : un raccourci « Valider » principal
 * exige que « Suivant » passe en secondaire ou que la barre du bas soit masquée.
 */
export function primaryConflict(template: Template): boolean {
  const layout = layoutOf(template);
  const shortcut = (layout?.header as Header | undefined)?.submit_shortcut;
  if (shortcut?.appearance !== 'primary' || shortcut.show === 'never') return false;
  return (layout?.footer as Footer | undefined)?.hidden !== true && shortcut.next_style !== 'demote';
}

/** « Valider » reste atteignable : barre du bas visible, ou raccourci du bandeau affiché. */
export function submitUnreachable(template: Template): boolean {
  const layout = layoutOf(template);
  if ((layout?.footer as Footer | undefined)?.hidden !== true) return false;
  return (layout?.header as Header | undefined)?.submit_shortcut?.show === 'never';
}
